import axios from 'axios'
import { Dialog, Toast, Notify } from 'vant'
import store from '@/store'
import { getDefaultLandingPath, getToken } from '@/utils/auth'

window.BASE_URL = process.env.VUE_APP_BASE_API

// create an axios instance
const service = axios.create({
  baseURL: process.env.VUE_APP_BASE_API, // url = base url + request url
  // withCredentials: true, // send cookies when cross-domain requests
  timeout: 300000, // request timeout
  headers: {
    platform: 'h5',
    version: '20250707'
  }
})

function tryHideFullScreenLoading() {
  Toast.clear()
}

function startLoading() {
  Toast({
    type: 'loading',
    duration: 0,
    forbidClick: true,
    message: '加载中...',
    loadingType: 'spinner'
  })
}

let sessionExpiredHandling = false

function createBusinessError(response) {
  const error = new Error((response && response.message) || '请求失败')
  error.code = response && response.code
  error.responseData = response
  return error
}

function getErrorMessage(error, fallbackMessage = '请求失败') {
  const responseData = error && error.response && error.response.data
  return (
    (error && error.responseData && error.responseData.message) ||
    (responseData && responseData.message) ||
    (error && error.message) ||
    fallbackMessage
  )
}

function redirectTo(path) {
  const targetHash = '#' + path
  if (window.location.hash !== targetHash) {
    window.location.hash = path
  }
}

function getAuthorizationToken(config) {
  if (!config || !config.headers) return ''
  const authorization = config.headers.Authorization || config.headers.authorization || ''
  const matches = String(authorization).match(/^Bearer\s+(\S+)$/i)
  return matches ? matches[1] : ''
}

/**
 * 已经使用新 Token 完成恢复时，忽略仍在途的旧 Token 请求返回，避免重复拉起钉钉认证。
 */
function isCurrentSessionRequest(config) {
  const requestToken = getAuthorizationToken(config)
  const currentToken = getToken() || ''
  return !requestToken || !currentToken || requestToken === currentToken
}

function handleSessionExpired(message) {
  if (sessionExpiredHandling) {
    return
  }

  sessionExpiredHandling = true
  store
    .dispatch('user/recoverDingSession')
    .then(result => {
      if (!result || result.status !== 'authenticated') {
        const error = new Error('当前环境无法使用钉钉自动登录，请使用密码登录')
        error.code = 'DING_RECOVERY_UNAVAILABLE'
        return Promise.reject(error)
      }

      // 按产品约定，会话恢复后进入默认首页，不重试导致失效的原业务请求。
      redirectTo(getDefaultLandingPath(result.userInfo))
      return result
    })
    .catch(error => {
      redirectTo('/login')
      Dialog.alert({
        title: '系统提示',
        message: getErrorMessage(error, message || '登录状态已失效，请使用密码登录')
      }).catch(() => {})
    })
    .finally(() => {
      sessionExpiredHandling = false
    })
}

// request interceptor
service.interceptors.request.use(
  config => {
    if (config.loading == undefined || config.loading !== false) {
      startLoading()
    }

    if (config.skipAuth) {
      delete config.headers.Authorization
      delete config.headers.authorization
    } else {
      const token = getToken()
      if (token) {
        config.headers.Authorization = 'Bearer ' + token
      }
    }
    return config
  },
  error => {
    console.log(error) // for debug
    tryHideFullScreenLoading()
    return Promise.reject(error)
  }
)

// response interceptor
service.interceptors.response.use(
  response => {
    tryHideFullScreenLoading()

    const res = response.data
    if ('arraybuffer' == response.request.responseType) {
      return response
    }
    if (res.code !== 200) {
      if (res.code === 50000) {
        if (!response.config.skipSessionRecovery && isCurrentSessionRequest(response.config)) {
          handleSessionExpired(res.message)
        }
        return Promise.reject(createBusinessError(res))
      }
      if (res.code == 60000) {
        Dialog.confirm({
          title: '系统提示',
          message: res.message
        })
          .then(() => {
            window.location.reload()
          })
          .catch(() => {})
      }
      Notify({ type: 'danger', message: res.message || 'Error' })
      return Promise.resolve(res)
    }
    return res
  },
  error => {
    tryHideFullScreenLoading()
    console.log('err' + error) // for debug
    const skipSessionRecovery = error.config && error.config.skipSessionRecovery
    if (
      error.response &&
      error.response.status === 401 &&
      !skipSessionRecovery &&
      isCurrentSessionRequest(error.config)
    ) {
      const responseData = error.response.data || {}
      handleSessionExpired(responseData.message || error.message)
    } else {
      Notify({ type: 'danger', message: getErrorMessage(error, '网络请求失败') })
    }
    return Promise.reject(error)
  }
)

export default service
